import { useState, useEffect } from "react";
import "./edit.css";
import { Button } from "react-bootstrap";
import axios from "axios";
import { Box } from "@mui/material";
import { useHistory } from "react-router-dom";
import { get_all_registered_students, delete_students, update_student_notification } from "services/api-service";
import { REACT_APP_API_URL, API_ROUTES } from "config/api-config";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Select from "react-select";
import { Search, X, Download } from "lucide-react";
import { downloadStudentData } from "services/downloadUtils";
import { MdArrowBack } from "react-icons/md";

export default function EditStudentDetails() {
  const theme = useTheme();
  const md_matches = useMediaQuery(theme.breakpoints.down("md"));
  const sm_matches = useMediaQuery(theme.breakpoints.down("sm"));
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const history = useHistory();

  const [filters, setFilters] = useState({
    name: "",
    id: "",
    gender: "",
    whom_to_meet: "", 
  });
  const [filteredStudents, setFilteredStudents] = useState([]);

  // Fetch unique staff names for the filter dropdown
  const [staffOptions, setStaffOptions] = useState([]);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    get_all_registered_students()
      .then((response) => {
        setStudents(response.data);
        setFilteredStudents(response.data);

        // Extract unique staff members the visitors came to meet
        const uniqueStaff = [...new Set(response.data.map(s => s.buying_behaviour || s.father_name).filter(Boolean))];
        setStaffOptions(uniqueStaff.map(staff => ({ value: staff, label: staff })));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    applyFilters();
  }, [students, filters]);

  const applyFilters = () => {
    let filtered = students;

    if (filters.name.trim()) {
      filtered = filtered.filter((student) =>
        student.first_name?.toLowerCase().includes(filters.name.toLowerCase()) ||
        student.last_name?.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    if (filters.id.trim()) {
      filtered = filtered.filter((student) =>
        student.student_id.toString().includes(filters.id)
      );
    }

    if (filters.gender.trim()) {
      filtered = filtered.filter((student) =>
        student.gender?.toLowerCase().includes(filters.gender.toLowerCase()) ||
        student.hostel_name?.toLowerCase().includes(filters.gender.toLowerCase())
      );
    }

    if (filters.whom_to_meet) {
      filtered = filtered.filter((student) =>
        student.buying_behaviour === filters.whom_to_meet || 
        student.father_name === filters.whom_to_meet
      );
    }

    setFilteredStudents(filtered);
  };

  const clearAllFilters = () => {
    setFilters({
      name: "",
      id: "",
      gender: "",
      whom_to_meet: "",
    });
    setFilteredStudents(students);
  };

  const hasActiveFilters = Object.values(filters).some((val) => val);

  const fetchdeletedata = async (student_id) => {
    const fullUrl = `${REACT_APP_API_URL}${API_ROUTES.delete_students}/${student_id}`;
    if (!REACT_APP_API_URL || !API_ROUTES.delete_students) {
      toast.error("API Configuration is missing!");
      return;
    }

    try {
      const token = localStorage.getItem("accessToken");
      await axios.delete(fullUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.warn("Visitor deleted");
      setTimeout(() => {
        fetchStudents(); 
      }, 1500);
    } catch (error) {
      console.error("Error deleting Visitor:", error);
      toast.error(error.message);
    }
  };

  const handleModifyfunc = async (student_id) => {
    const student = students.find((s) => s.student_id === student_id);
    history.push(`/admin/bra`, {
      student_id,
      gender: student?.gender || student?.hostel_name,
      whom_to_meet: student?.buying_behaviour || student?.father_name,
      reason_to_meet: student?.pricing || student?.mother_name,
      email: student?.email,
    });
  };

  const handleNotificationToggle = async (student_id, currentStatus) => {
    const isCurrentlyChecked = currentStatus === true || currentStatus === 1 || currentStatus === "1";
    const newStatus = !isCurrentlyChecked;

    setFilteredStudents(prev => prev.map(s => 
      s.student_id === student_id ? { ...s, is_notification: newStatus } : s
    ));
    setStudents(prev => prev.map(s => 
      s.student_id === student_id ? { ...s, is_notification: newStatus } : s
    ));

    try {
      await update_student_notification(student_id, newStatus);
      if (newStatus) {
        toast.success("🔔 Notifications ENABLED for this Visitor");
      } else {
        toast.warn("🔕 Notifications DISABLED for this Visitor");
      }
    } catch (error) {
      console.error("Failed to update notification status:", error);
      toast.error("Failed to save setting. Reverting...");
      fetchStudents(); 
    }
  };

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try { await downloadStudentData(filteredStudents, "excel"); toast.success("Excel downloaded!"); } 
    catch (error) { toast.error("Failed to download Excel"); } 
    finally { setDownloading(false); }
  };
  const handleDownloadPDF = async () => {
    setDownloading(true);
    try { await downloadStudentData(filteredStudents, "pdf"); toast.success("PDF downloaded!"); } 
    catch (error) { toast.error("Failed to download PDF"); } 
    finally { setDownloading(false); }
  };
  const handleDownloadCSV = async () => {
    setDownloading(true);
    try { await downloadStudentData(filteredStudents, "csv"); toast.success("CSV downloaded!"); } 
    catch (error) { toast.error("Failed to download CSV"); } 
    finally { setDownloading(false); }
  };

  const studentIdOptions = students
    .filter((s) => s.student_id)
    .map((s) => ({ value: s.student_id.toString(), label: s.student_id.toString() }));

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
  ];

  const customSelectStyles = {
    control: (provided) => ({
      ...provided, minWidth: "150px", borderRadius: "8px", border: "2px solid #ddd", padding: "2px", fontSize: "14px",
    }),
    option: (provided, state) => ({
      ...provided, backgroundColor: state.isSelected ? "#007bff" : "white", color: state.isSelected ? "white" : "black", padding: "8px", cursor: "pointer",
    }),
    menu: (provided) => ({ ...provided, zIndex: 50 }),
    menuPortal: (provided) => ({ ...provided, zIndex: 50 }),
  };

  return (
    <main className="emotionContainer">
      <div style={{ width: "100%", justifyContent: "center", fontFamily: "-moz-initial" }}>
        <button 
          onClick={() => history.push("/admin/FaceDetails")}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '600',
            marginBottom: '20px',
            padding: 0
          }}
        >
          <MdArrowBack size={20} />
          Back to Member Menu
        </button>

        {/* DOWNLOAD BUTTONS */}
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", padding: "15px 20px", backgroundColor: "#f0f7ff", borderRadius: "8px", marginBottom: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
          <Download size={20} style={{ color: "#0066cc" }} />
          <h3 style={{ margin: 0, color: "#333", fontSize: "16px" }}>Download Report</h3>
          
          <div style={{ marginLeft: "auto", display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <Button disabled={downloading || filteredStudents.length === 0} onClick={handleDownloadExcel} style={{ backgroundColor: "#28a745", color: "white", fontWeight: "bold", borderRadius: "6px", padding: "8px 16px", border: "none", cursor: downloading ? "not-allowed" : "pointer", opacity: downloading || filteredStudents.length === 0 ? 0.6 : 1, fontSize: "13px" }}>📊 Excel</Button>
            <Button disabled={downloading || filteredStudents.length === 0} onClick={handleDownloadPDF} style={{ backgroundColor: "#dc3545", color: "white", fontWeight: "bold", borderRadius: "6px", padding: "8px 16px", border: "none", cursor: downloading ? "not-allowed" : "pointer", opacity: downloading || filteredStudents.length === 0 ? 0.6 : 1, fontSize: "13px" }}>📄 PDF</Button>
            <Button disabled={downloading || filteredStudents.length === 0} onClick={handleDownloadCSV} style={{ backgroundColor: "#0066cc", color: "white", fontWeight: "bold", borderRadius: "6px", padding: "8px 16px", border: "none", cursor: downloading ? "not-allowed" : "pointer", opacity: downloading || filteredStudents.length === 0 ? 0.6 : 1, fontSize: "13px" }}>📋 CSV</Button>
          </div>
          {downloading && <div style={{ color: "#0066cc", fontSize: "13px", fontWeight: "500", marginLeft: "auto" }}>⏳ Preparing...</div>}
        </div>

        {/* ADVANCED FILTERS */}
        <div style={{ backgroundColor: "#f8f9fa", padding: "15px 20px", borderRadius: "8px", marginBottom: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", position: "relative", zIndex: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <Search size={20} style={{ color: "#666" }} />
            <h3 style={{ margin: 0, color: "#333" }}>Advanced Filters</h3>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ fontSize: "12px", color: "#666", fontWeight: "500" , padding: "3px 10px", margin : "5px"}}>Search by Name</label>
              <input type="text" placeholder="Type name" value={filters.name} onChange={(e) => setFilters({ ...filters, name: e.target.value })} style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "2px solid #ddd", fontSize: "14px", boxSizing: "border-box", marginTop : "5px" }} />
            </div>

            <div>
              <label style={{ fontSize: "12px", color: "#666", fontWeight: "500" , padding : "3px 10px" , margin : "5px" }}>Search by Visitor ID</label>
              <Select styles={customSelectStyles} options={studentIdOptions} isClearable isSearchable placeholder="Type or select ID..." menuPortalTarget={document.body} value={studentIdOptions.find((opt) => opt.value === filters.id) || null} onChange={(selected) => setFilters({ ...filters, id: selected?.value || "" })} />
            </div>

            <div>
              <label style={{ fontSize: "12px", color: "#666", fontWeight: "500", padding : "3px 10px" , margin : "5px" }}>Filter by Gender</label>
              <Select styles={customSelectStyles} options={genderOptions} placeholder="All genders" isClearable menuPortalTarget={document.body} value={genderOptions.find((opt) => opt.value === filters.gender) || null} onChange={(selected) => setFilters({ ...filters, gender: selected?.value || "" })} isSearchable={false} />
            </div>

            <div>
              <label style={{ fontSize: "12px", color: "#666", fontWeight: "500", padding : "3px 10px" , margin : "5px" }}>Filter by Host (Staff)</label>
              <Select styles={customSelectStyles} options={staffOptions} placeholder="Select staff..." isClearable menuPortalTarget={document.body} value={staffOptions.find((opt) => opt.value === filters.whom_to_meet) || null} onChange={(selected) => setFilters({ ...filters, whom_to_meet: selected?.value || "" })} isSearchable={true} />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {hasActiveFilters && (
              <button onClick={clearAllFilters} style={{ padding: "8px 16px", backgroundColor: "#ff6b6b", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontWeight: "500" }}>
                <X size={16} /> Clear All Filters
              </button>
            )}
            <span style={{ marginLeft: "auto", color: "#666", fontSize: "14px", fontWeight: "500" }}>
              Showing {filteredStudents.length} of {students.length} Visitor
            </span>
          </div>
        </div>

        {/* TABLE */}
        <table>
          <thead style={{ borderRadius: "10%" }}>
            <tr>
              <th>Image</th>
              <th>Date</th>
              <th>Visitor ID</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Contact No.</th>
              <th>Gender</th>
              <th>Whom to Meet</th>
              <th>Reason to Meet</th>           
              <th>Notify</th>
              <th>Edit</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(filteredStudents) && filteredStudents.length > 0 ? (
              filteredStudents.map((student) => {
                return (
                  <tr key={student.student_id}>
                    <td className="es-th">
                      {student.has_image ? (
                        <img 
                          src={`${REACT_APP_API_URL}/${API_ROUTES.get_student_image(student.student_id)}`} 
                          width="60%" 
                          height="100%" 
                          alt={`${student.first_name}`} 
                          loading="lazy"
                          onClick={() => setSelectedImageId(student.student_id)} 
                          style={{ cursor: "pointer", objectFit: "cover" }} 
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      ) : (
                        <div style={{ width: "60%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f0f0f0", borderRadius: "4px", color: "#999", fontSize: "12px", fontWeight: "500", padding: "8px", textAlign: "center" }}>No photo</div>
                      )}
                    </td>
                    <td className="stud-date">{student.created_at || student.date}</td>
                    <td><strong>{student.student_id}</strong></td>
                    <td>{student.first_name}</td>
                    <td>{student.last_name}</td>
                    <td>{student.email || "N/A"}</td>
                    <td>{student.mobile_no}</td>
                    <td>
                      <span style={{ padding: "4px 8px", borderRadius: "4px", backgroundColor: student.gender === "Male" || student.hostel_name === "Male" ? "#e3f2fd" : "#fce4ec", color: student.gender === "Male" || student.hostel_name === "Male" ? "#1976d2" : "#c2185b", fontWeight: "500", fontSize: "12px" }}>
                        {student.gender || student.hostel_name || "N/A"}
                      </span>
                    </td>
                    <td><span style={{ fontSize: "13px", fontWeight: "600", color: "#d97706" }}>{student.buying_behaviour || student.father_name || "N/A"}</span></td>
                    <td><span style={{ fontSize: "13px", fontWeight: "500", color: "#333" }}>{student.pricing || student.mother_name || "N/A"}</span></td>
                    
                    <td>
                      <input 
                        type="checkbox" 
                        checked={student.is_notification === true || student.is_notification === 1 || student.is_notification === "1"} 
                        onChange={() => handleNotificationToggle(student.student_id, student.is_notification)} 
                        style={{ width: "18px", height: "18px", cursor: "pointer" }} 
                      />
                    </td>
                    <td>
                      <Button onClick={() => handleModifyfunc(student.student_id)} style={{ backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "4px", padding: "6px 12px", cursor: "pointer", fontSize: "13px", fontWeight: "500" }}>Edit</Button>
                    </td>
                    <td>
                      <Button onClick={() => fetchdeletedata(student.student_id)} style={{ backgroundColor: "#dc3545", color: "white", border: "none", borderRadius: "4px", padding: "6px 12px", cursor: "pointer", fontSize: "13px", fontWeight: "500" }}>Delete</Button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan="13" style={{ textAlign: "center", padding: "20px", color: "#999", fontWeight: "500" }}>No Visitors found matching your filters</td></tr>
            )}
          </tbody>
        </table>

        {selectedImageId && (
          <div onClick={() => setSelectedImageId(null)} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
            <img 
              src={`${REACT_APP_API_URL}/${API_ROUTES.get_student_image(selectedImageId)}`} 
              alt="Enlarged" 
              style={{ width: "30%", height: "auto", borderRadius: "8px", objectFit: "contain" }} 
            />
          </div>
        )}
      </div>
    </main>
  );
}
